import * as React from 'react';
import { cloneElement, ReactElement, Fragment } from 'react';
import { Divider, List, ListItem, ListItemText, styled } from '@mui/material';
import { useTranslate } from 'react-admin';

import { SearchPanelProps } from './types';
import { SearchResultItem } from './SearchResultItem';
import { SearchResultsGroup } from './SearchResultsGroup';
import { groupSearchResultsByResource } from './groupSearchResultsByResource';
import { useSearchResults } from './useSearchResults';
import { useArrowKeysToNavigate } from './useArrowKeysToNavigate';

/**
 * A component which displays search results inside a MUI [`<List>`](https://mui.com/components/lists),
 * grouping results by targets when they are multiple targets.
 *
 * @param props {SearchPanelProps}
 * @param props.children {ReactElement} A ReactElement which will be cloned for each search result item.
 */
export const SearchResultsPanel = (props: SearchPanelProps): ReactElement => {
    const listRef = React.useRef<HTMLUListElement>(null);
    const { children = <SearchResultItem />, ...rest } = props;
    useArrowKeysToNavigate(listRef);

    const translate = useTranslate();

    const { data, onClose } = useSearchResults();

    if (!data || data.length === 0) {
        return (
            <Root dense {...rest}>
                <ListItem>
                    <ListItemText
                        primary={translate('ra.navigation.no_results', {
                            _: 'No results',
                        })}
                    />
                </ListItem>
            </Root>
        );
    }

    const groupedData = groupSearchResultsByResource(data, translate);

    if (groupedData.length === 1) {
        return (
            // @ts-ignore
            <Root component="nav" dense ref={listRef} {...rest}>
                {data.map(searchResultItem => {
                    return cloneElement(children, {
                        key: searchResultItem.id,
                        data: searchResultItem,
                        onClose,
                    });
                })}
            </Root>
        );
    }

    return (
        // @ts-ignore
        <Root component="nav" dense ref={listRef} {...rest}>
            {groupedData.map((group, index) => (
                <Fragment key={group.label}>
                    <SearchResultsGroup
                        label={group.label}
                        data={group.data}
                        onClose={onClose}
                    >
                        {children}
                    </SearchResultsGroup>
                    {index < groupedData.length - 1 && (
                        <Divider sx={{ my: 1 }} />
                    )}
                </Fragment>
            ))}
        </Root>
    );
};

const PREFIX = 'RaSearchResultsPanel';

const Root = styled(List, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})({
    maxHeight: 'calc(100vh - 100px)',
    minWidth: 216,
});
